import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { memberFetch, clearMemberToken, getMemberToken } from "../lib/memberApi";
import { API } from "../lib/api";
import { toast } from "sonner";
import {
  LogOut, Salad, BookOpen, LineChart, Dumbbell, FileText, FileDown, Plane,
} from "lucide-react";

import { GOALS } from "../components/member/_shared";
import NutritionTab from "../components/member/NutritionTab";
import WorkoutsTab from "../components/member/WorkoutsTab";
import BillingTab from "../components/member/BillingTab";
import LibraryTab from "../components/member/LibraryTab";
import ProgressTab from "../components/member/ProgressTab";
import OneOnOneStatusBanner from "../components/member/OneOnOneStatusBanner";
import WaiverModal from "../components/member/WaiverModal";
import MyAccountPanel from "../components/member/MyAccountPanel";
import PwaInstallPrompt from "../components/member/PwaInstallPrompt";
import RequestSessionModal from "../components/member/RequestSessionModal";

const ALL_TABS = [
  { id: "nutrition", label: "Nutrition", Icon: Salad, feature: "meal_plans" },
  { id: "workouts", label: "Workouts", Icon: Dumbbell, feature: "workouts" },
  { id: "billing", label: "Billing", Icon: FileText, feature: "billing" },
  { id: "library", label: "Library", Icon: BookOpen, feature: "meal_plans" },
  { id: "progress", label: "Progress", Icon: LineChart, feature: "progress" },
];

export default function MemberDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("nutrition");
  const [goal, setGoal] = useState("muscle_gain");
  const [foodsByGoal, setFoodsByGoal] = useState({ weight_loss: [], muscle_gain: [], fat_loss: [] });
  const [plan, setPlan] = useState(null);
  const [showRequestSession, setShowRequestSession] = useState(false);

  const loadMe = async () => {
    const m = await memberFetch("/member/me");
    setMe(m);
    // 1-on-1 applicants (pending or denied) and Square-Basic awaiters should
    // stay on the dashboard and see their status banner, NOT be forced to
    // the checkout page.
    const lr = m.latest_request;
    const hasOpenRequest = lr && (lr.status === "pending" || lr.status === "denied");
    const awaitingSquare = (m.pending_payment_method || "").startsWith("square_");
    if (!m.active && !hasOpenRequest && !awaitingSquare) { navigate("/membership"); return; }
    // Migrate any legacy comma-joined entries into individual chips.
    const normalize = (arr) => {
      const out = [];
      for (const item of arr || []) {
        for (const t of String(item).split(/[,\n]+/).map((s) => s.trim()).filter(Boolean)) {
          if (!out.includes(t)) out.push(t);
        }
      }
      return out.slice(0, 30);
    };
    setFoodsByGoal({
      weight_loss: normalize(m.favorites?.weight_loss),
      muscle_gain: normalize(m.favorites?.muscle_gain),
      fat_loss: normalize(m.favorites?.fat_loss),
    });
    if (m.active_goal && GOALS.find((g) => g.id === m.active_goal)) setGoal(m.active_goal);
  };

  // Reflect the active tab in the browser tab title — Gain Time · Workouts, etc.
  useEffect(() => {
    const activeLabel = ALL_TABS.find((t) => t.id === tab)?.label;
    document.title = activeLabel ? `Gain Time · ${activeLabel}` : "Gain Time Fitness";
    return () => { document.title = "Gain Time Fitness"; };
  }, [tab]);

  useEffect(() => {
    if (!getMemberToken()) { navigate("/member/login"); return; }
    (async () => {
      try {
        await loadMe();
        const plans = await memberFetch("/member/meal-plans");
        if (plans.length > 0) setPlan(plans[0]);
      } catch { navigate("/member/login"); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const logout = () => { clearMemberToken(); navigate("/"); };

  const downloadOwnWaiver = async () => {
    try {
      const token = getMemberToken();
      const res = await fetch(`${API}/member/waiver/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "GainTimeFitness-Waiver.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Waiver PDF downloaded");
    } catch (e) {
      toast.error(e.message || "Could not download waiver");
    }
  };

  const downloadWeekPacket = async (include = "both") => {
    const labels = { both: "packet", workouts: "workouts", meals: "meal plan" };
    try {
      const token = getMemberToken();
      toast.info(`Building your ${labels[include]}...`);
      const res = await fetch(`${API}/member/print-packet/pdf?days=7&include=${include}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      const fname = { both: "Packet", workouts: "Workouts", meals: "Meals" }[include];
      a.download = `GainTime-7Day-${fname}-${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Your ${labels[include]} is downloaded.`);
    } catch (e) {
      toast.error(e.message || "Could not build packet");
    }
  };

  if (!me) return <div className="min-h-screen bg-[#0A0A0A] grid place-items-center text-zinc-500">Loading...</div>;
  const needsWaiver = me.active && !me.waiver_signed_at;
  const subUntil = me.subscription_until ? new Date(me.subscription_until).toLocaleDateString() : null;
  const features = me.features || [];
  const TABS = ALL_TABS.filter((t) => features.includes(t.feature));
  const currentTab = TABS.find((t) => t.id === tab) ? tab : (TABS[0]?.id || "nutrition");

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="member-dashboard">
      {needsWaiver && (
        <WaiverModal memberName={me.name} onSigned={loadMe} />
      )}
      <header className="border-b border-zinc-900 sticky top-0 bg-[#0A0A0A]/95 backdrop-blur z-30">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12 h-16 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img src="https://customer-assets.emergentagent.com/job_houston-training-hub/artifacts/5f1feyf5_1781429554579.png" alt="Gain Time Fitness" className="w-8 h-8 object-cover shrink-0" />
            <span className="font-display text-sm sm:text-lg tracking-wider truncate">
              <span className="hidden sm:inline">MEMBER · GAIN TIME</span>
              <span className="sm:hidden">MEMBER</span>
            </span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <div className="hidden md:block text-right">
              <div className="text-xs">{me.name}</div>
              <div className="text-[10px] tracking-widest uppercase text-zinc-500">Active until {subUntil}</div>
            </div>
            {localStorage.getItem("gt_token") && (
              <Link
                to="/admin"
                data-testid="member-to-admin-link"
                className="inline-flex items-center gap-2 px-2 sm:px-3 py-2 text-xs font-bold tracking-[0.2em] uppercase border border-[#E5B43A] text-[#E5B43A] hover:bg-[#E5B43A] hover:text-white transition-colors"
              >
                Admin
              </Link>
            )}
            <button onClick={logout} className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 hover:text-[#E5B43A]" data-testid="member-logout">
              <LogOut size={14} /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto px-4 md:px-12 flex gap-1 overflow-x-auto border-t border-zinc-900">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`member-tab-${id}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-xs font-bold tracking-[0.2em] uppercase border-b-2 transition-colors whitespace-nowrap ${tab === id ? "border-[#E5B43A] text-white" : "border-transparent text-zinc-500 hover:text-white"}`}
            >
              <Icon size={14} /> {label}
              {id === "progress" && me.due_for_checkin && (
                <span className="ml-1 inline-block w-2 h-2 rounded-full bg-[#E5B43A]" />
              )}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 md:px-12 py-10">
        <PwaInstallPrompt />

        <div className="mb-10">
          <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#E5B43A] mb-3">// Member Hub</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight">Welcome back, <span className="text-[#E5B43A]">{me.name.split(' ')[0]}.</span></h1>
        </div>

        <OneOnOneStatusBanner me={me} refreshMe={loadMe} />

        {me.access_scope && me.access_scope !== "full" && (
          <div className="mb-6 border border-[#E5B43A]/60 bg-[#E5B43A]/10 px-5 py-3 text-sm" data-testid="access-scope-notice">
            <span className="text-[#E5B43A] font-bold tracking-widest uppercase text-[10px] mr-2">Your Plan</span>
            <span className="text-zinc-200">
              {me.access_scope === "workouts_only" && "This plan includes Workouts only."}
              {me.access_scope === "meals_only" && "This plan includes Meal Plans only."}
            </span>
          </div>
        )}

        {(me.tier === "one_on_one" || (typeof me.sessions_quota === "number" && me.sessions_quota > 0)) && (
          <div className="mb-8 border border-zinc-800 bg-[#141414] px-5 py-4 flex items-center justify-between flex-wrap gap-3" data-testid="member-session-tracker">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#E5B43A] mb-1">
                Training Sessions · Last 30 Days
                {me.tier !== "one_on_one" && (
                  <span className="ml-2 text-zinc-500 normal-case tracking-normal">(in-person or Zoom)</span>
                )}
              </div>
              <div className="font-display text-3xl">
                {me.session_count || 0}
                {typeof me.sessions_quota === "number" && (
                  <span className="text-zinc-500 text-xl"> / {me.sessions_quota}</span>
                )}
                {me.sessions_quota === null && (
                  <span className="text-zinc-500 text-xs ml-2 align-middle">unlimited</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => setShowRequestSession(true)}
                data-testid="request-session-button"
                className="inline-flex items-center gap-2 bg-[#E5B43A] text-black px-4 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-white transition-colors"
              >
                Request a Session
              </button>
              <div className="text-right">
                <div className="text-[10px] tracking-widest uppercase text-zinc-500">Updated by Davon · view only</div>
                {(me.sessions_recent || []).length > 0 && (
                  <div className="mt-1 text-xs text-zinc-400">
                    Last: {new Date(me.sessions_recent[0].redeemed_at).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {!me.active ? null : (
          <>
            {tab === "nutrition" && currentTab === "nutrition" && (
              <NutritionTab me={me} goal={goal} setGoal={setGoal} foodsByGoal={foodsByGoal} setFoodsByGoal={setFoodsByGoal} plan={plan} setPlan={setPlan} refreshMe={loadMe} />
            )}
            {tab === "workouts" && currentTab === "workouts" && features.includes("workouts") && <WorkoutsTab me={me} refreshMe={loadMe} />}
            {tab === "billing" && currentTab === "billing" && features.includes("billing") && <BillingTab />}
            {tab === "library" && currentTab === "library" && <LibraryTab foodsByGoal={foodsByGoal} />}
            {tab === "progress" && currentTab === "progress" && features.includes("progress") && <ProgressTab me={me} refreshMe={loadMe} />}
          </>
        )}

        <div className="mt-12 border border-zinc-900 bg-[#0F0F0F] p-5 md:p-6" data-testid="vacation-packet-row">
          <div className="flex items-center gap-2 mb-1">
            <Plane size={14} className="text-[#E5B43A]" />
            <h3 className="font-display text-lg">Print to PDF</h3>
          </div>
          <p className="text-xs text-zinc-500 leading-relaxed max-w-md mb-4">
            Download your week off-grid. PDFs include cues, sets/reps, and your email
            watermark on every page.
          </p>
          <div className={`grid gap-2 ${features.includes("workouts") && features.includes("meal_plans") ? "sm:grid-cols-3" : "sm:grid-cols-1"}`}>
            {features.includes("workouts") && (
              <button
                onClick={() => downloadWeekPacket("workouts")}
                data-testid="download-workouts-pdf"
                className="inline-flex items-center justify-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-300 border border-zinc-700 px-3 py-3 hover:border-[#E5B43A] hover:text-[#E5B43A] transition-colors"
              >
                <FileDown size={11} /> 7-Day Workouts
              </button>
            )}
            {features.includes("meal_plans") && (
              <button
                onClick={() => downloadWeekPacket("meals")}
                data-testid="download-meals-pdf"
                className="inline-flex items-center justify-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-300 border border-zinc-700 px-3 py-3 hover:border-[#E5B43A] hover:text-[#E5B43A] transition-colors"
              >
                <FileDown size={11} /> Meal Plan
              </button>
            )}
            {features.includes("workouts") && features.includes("meal_plans") && (
              <button
                onClick={() => downloadWeekPacket("both")}
                data-testid="download-packet-pdf"
                className="inline-flex items-center justify-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase text-black bg-[#E5B43A] border border-[#E5B43A] px-3 py-3 hover:bg-white transition-colors"
              >
                <FileDown size={11} /> Full Packet
              </button>
            )}
          </div>
        </div>

        {me.waiver_signed_at && (
          <div className="mt-16 pt-8 border-t border-zinc-900 flex items-center justify-between flex-wrap gap-3" data-testid="member-waiver-footer">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-600">
              Liability Waiver Signed · {new Date(me.waiver_signed_at).toLocaleDateString()}
            </div>
            <button
              onClick={downloadOwnWaiver}
              data-testid="member-download-waiver"
              className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-400 border border-zinc-800 px-3 py-2 hover:border-[#E5B43A] hover:text-[#E5B43A] transition-colors"
            >
              <FileDown size={11} /> Download My Waiver
            </button>
          </div>
        )}

        <MyAccountPanel me={me} refreshMe={loadMe} />
      </main>

      {showRequestSession && (
        <RequestSessionModal
          onClose={() => setShowRequestSession(false)}
          onSent={loadMe}
        />
      )}
    </div>
  );
}
